import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, LabelList } from 'recharts';
import { FaChartLine, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const StatCard = ({ icon: Icon, title, value, subtitle, colorClasses }) => (
  <div className={`p-4 rounded-xl text-center shadow ${colorClasses.bg}`}>
    <div className="flex items-center justify-center mb-2 text-2xl text-gray-700">
      <Icon className={`mr-2 ${colorClasses.icon}`} />
      <span className="text-sm font-medium text-gray-500">{title}</span>
    </div>
    <p className="text-lg font-semibold text-gray-800">{subtitle}</p>
    <p className={`text-2xl font-bold ${colorClasses.text}`}>{value ?? '--'}%</p>
  </div>
);

const ScopeProductivityChart = ({ filters }) => {
  const { accessToken } = useAuth();
  const [chartData, setChartData] = useState([]);
  const [average, setAverage] = useState(0);
  const [maxScope, setMaxScope] = useState(null);
  const [minScope, setMinScope] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const buildParams = (filters) => {
    const params = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        params[key] = filters[key];
      }
    });
    return params;
  };

  useEffect(() => {
    const fetchScopeProductivity = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrl}/superAdminDashboard/getScopeWiseBiometricProductivity`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: buildParams(filters),
        });

        if (response.data && response.data.success) {
          const productivityData = response.data.data || [];

          // Map the API response to the chartData format
          const mappedData = productivityData
            .filter((item) => item.scopeId && item.biometricPercentage)
            .map((item) => ({
              scope: item.scopeId,
              productivity: parseFloat(item.biometricPercentage.replace('%', '').trim()) || 0,
            }));

          setChartData(mappedData);

          // Calculate average, max, and min
          const total = mappedData.reduce((sum, item) => sum + item.productivity, 0);
          setAverage((total / mappedData.length).toFixed(2));

          const max = mappedData.reduce((a, b) => (a.productivity > b.productivity ? a : b));
          const min = mappedData.reduce((a, b) => (a.productivity < b.productivity ? a : b));
          setMaxScope(max);
          setMinScope(min);
        }
      } catch (error) {
        console.error('Error fetching scope productivity:', error);
      } finally {
        setLoading(false);
      }
    };

    if (filters) {
      fetchScopeProductivity();
    }
  }, [accessToken, filters]);

  const ChartContent = ({ zoomed = false, scrollable = false }) => {
    const barSize = zoomed ? 50 : 30;
    const labelSpacing = zoomed ? 100 : 70;
    const chartWidth = chartData.length * labelSpacing;

    return (
      <div style={{ width: '100%', overflowX: scrollable ? 'auto' : 'visible' }}>
        <div
          style={{
            width: scrollable ? `${chartWidth}px` : '100%',
            height: zoomed ? 600 : 400,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barSize={barSize} margin={{ top: 20, right: 30, left: 30, bottom: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="scope"
                interval={0}
                angle={-45}
                textAnchor="end"
                tick={{fill: '#333', fontSize: zoomed ? 16 : 12 }}
                label={{
                  value: 'Scope',
                  position: 'bottom',
                  offset: -5,
                  dy: 90,
                  style: { fontWeight: 'bold', fill: '#333', fontSize: zoomed ? 18 : 14 },
                }}
              />
              <YAxis
                label={{
                  value: 'Productivity %',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 10,
                  style: { fontWeight: 'bold', fill: '#333', fontSize: zoomed ? 18 : 14 },
                }}
                tick={{fill: '#333', fontSize: zoomed ? 14 : 12 }}
              />
              <Tooltip
                cursor={{ fill: 'transparent' }}
                formatter={(value) => [`${value}%`, 'Productivity']}
                labelFormatter={(label) => `Scope: ${label}`}
              />
              <Bar dataKey="productivity" fill="#1A2B5C" isAnimationActive={false}>
                <LabelList
                  dataKey="productivity"
                  position="top"
                  formatter={(val) => `${val}%`}
                  style={{ fontSize: zoomed ? 14 : 12 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-md p-6 mt-10 relative">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Scope-wise Productivity Analysis</h2>
          <button
            onClick={() => setFocusMode(true)}
            className="px-4 py-1.5 text-sm rounded-md bg-[#1A2B5C] text-white hover:bg-[#EE1D23] transition"
          >
            Focus Mode
          </button>
        </div>

        {/* Stat Cards */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <StatCard
              icon={FaChartLine}
              title="Average Productivity"
              subtitle=""
              value={average}
              colorClasses={{
                bg: 'bg-[#F0F4FA]', // light blue background
                text: 'text-[#1A2B5C]', // blue text
                icon: 'text-[#1A2B5C]', // blue icon
              }}
            />
            <StatCard
              icon={FaArrowUp}
              title="Highest Productivity"
              subtitle={maxScope?.scope}
              value={maxScope?.productivity}
              colorClasses={{
                bg: 'bg-[#EAF4F4]', // subtle light blue/gray
                text: 'text-[#1A2B5C]', // blue text
                icon: 'text-[#1A2B5C]', // blue icon
              }}
            />
            <StatCard
              icon={FaArrowDown}
              title="Lowest Productivity"
              subtitle={minScope?.scope}
              value={minScope?.productivity}
              colorClasses={{
                bg: 'bg-[#FDEBEB]', // soft red/pink background
                text: 'text-[#EE1D23]', // red text
                icon: 'text-[#EE1D23]', // red icon
              }}
            />
          </div>
        )}

        {/* Chart */}
        <div style={{ width: '100%', height: 400 }}>
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
            </div>
          ) : (
            <ChartContent />
          )}
        </div>
      </div>

      {/* Focus Mode */}
      {focusMode && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative w-screen min-h-screen bg-white rounded-3xl p-8 shadow-2xl overflow-y-auto">
            <button
              onClick={() => setFocusMode(false)}
              className="absolute top-4 right-4 bg-white rounded-full shadow p-2 hover:text-[#EE1D23] transition"
              title="Exit Focus Mode"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <h2 className="text-2xl font-bold mb-6 text-center text-[#1A2B5C]">Scope-wise Productivity (Focus Mode)</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <StatCard
                icon={FaChartLine}
                title="Average Productivity"
                subtitle=""
                value={average}
                colorClasses={{
                  bg: 'bg-[#F0F4FA]', // light blue background
                  text: 'text-[#1A2B5C]', // blue text
                  icon: 'text-[#1A2B5C]', // blue icon
                }}
              />
              <StatCard
                icon={FaArrowUp}
                title="Highest Productivity"
                subtitle={maxScope?.scope}
                value={maxScope?.productivity}
                colorClasses={{
                  bg: 'bg-[#EAF4F4]', // subtle light blue/gray
                  text: 'text-[#1A2B5C]', // blue text
                  icon: 'text-[#1A2B5C]', // blue icon
                }}
              />
              <StatCard
                icon={FaArrowDown}
                title="Lowest Productivity"
                subtitle={minScope?.scope}
                value={minScope?.productivity}
                colorClasses={{
                  bg: 'bg-[#FDEBEB]', // soft red/pink background
                  text: 'text-[#EE1D23]', // red text
                  icon: 'text-[#EE1D23]', // red icon
                }}
              />
            </div>

            <div style={{ width: '100%', height: 500 }}>
              <ChartContent zoomed={true} scrollable={true} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScopeProductivityChart;
